import { createCable } from "@anycable/web";
import * as Sentry from "@sentry/nextjs";

import { apiCache } from "@/api/apiCache";
import { getAuthToken } from "@/api/client";
import { firebaseAuth } from "@/utils/firebaseAuth";
import { createLogger } from "@/utils/logger";
import { isInsideMobileWebView } from "@/utils/mobileBridgeAuth";
import { getWebSocketUrl } from "@/utils/yjs/provider";

import type { Channels, MessagePayload } from "./types";
import type { Transport } from "@anycable/core";

const logger = createLogger("Cable");

function isAuthenticated(): boolean {
  // In the mobile WebView auth lives on the native side; the bridge satisfies
  // tokenRefresher, so treat the WebView as authenticated here.
  return !!firebaseAuth.currentUser || isInsideMobileWebView();
}

async function tokenRefresher(transport: Transport) {
  try {
    if (!isAuthenticated()) {
      logger.debug("Cable tokenRefresher: skipping, no authenticated user");
      return;
    }

    logger.debug("Cable tokenRefresher refreshing token");
    const token = await getAuthToken(true);
    if (token) {
      logger.debug("Cable tokenRefresher setting token");
      transport.setToken(token, "token");
    } else {
      logger.debug("Cable tokenRefresher: no Firebase user token available");
      Sentry.captureMessage("Cable tokenRefresher: no Firebase token", {
        level: "warning",
        contexts: cableContexts(),
      });
    }
  } catch (error) {
    logger.debug("Cable tokenRefresher failed", error);
    Sentry.captureException(error, { contexts: cableContexts() });
  }
}

let cableIsConnected = false;
let cableIsConnecting = false;

// We create the cable lazily to avoid establishing a websocket connection
// (and sending subscription requests) before Firebase auth is ready.
// This prevents long-lived "broken" connections created without a token.
let cableInstance: ReturnType<typeof createCable> | undefined;

export function hasActiveSubscriptions(): boolean {
  return (cableInstance?.hub.size ?? 0) > 0;
}

function getVisibilityState(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.visibilityState;
}

function getTransport(cable: ReturnType<typeof createCable>): Transport | undefined {
  // `@anycable/web` doesn't currently expose transport in its public types,
  // but it's available at runtime and is required for manual token refresh.
  return (cable as unknown as { transport?: Transport }).transport;
}

function isVisibleAndOnline() {
  const online = typeof navigator === "undefined" ? true : navigator.onLine;
  const visible = getVisibilityState() !== "hidden";
  return online && visible;
}

export function pauseCable() {
  // Stop reconnect/backoff while the tab is hidden/offline.
  // AnyCable's internal backoff can grow to many minutes/hours otherwise.
  try {
    logger.debug("Cable pauseCable disconnecting");
    cableInstance?.disconnect?.();
    logger.debug("Cable pauseCable disconnected");
  } catch (error) {
    logger.debug("Cable pauseCable failed", error);
    Sentry.captureException(error, { contexts: cableContexts() });
  } finally {
    cableIsConnected = false;
    cableIsConnecting = false;
  }
}

export async function kickCable() {
  if (cableIsConnected || cableIsConnecting) return;

  // don’t even try without auth (in the mobile WebView auth lives on the
  // native side; the bridge will satisfy tokenRefresher)
  if (!isAuthenticated()) return;

  if (!hasActiveSubscriptions()) return;

  // Don't attempt to connect in the background; it just burns through retries/backoff.
  if (!isVisibleAndOnline()) return;

  cableIsConnecting = true;

  const cable = getCable();
  try {
    // Ensure we refresh token before connecting (Firebase tokens expire ~hourly).
    // Relying purely on AnyCable internal token refresh can lead to flapping reconnects
    // with no explicit JS exception.
    if (cable) {
      const transport = getTransport(cable);
      if (transport) {
        await tokenRefresher(transport);
      }
    }
    logger.debug("Cable kickCable connecting");
    await cable?.connect?.();
    logger.debug("Cable kickCable connected");
  } catch (error) {
    logger.debug("Cable kickCable failed", error);
    Sentry.captureException(error, { contexts: cableContexts() });
    // If AnyCable transport is stuck in a closed state, a disconnect+connect often resets it.
    // We do this exactly once to avoid infinite loops.
    const err = error as { name?: string; message?: string };
    const isDisconnectedError =
      err?.name === "DisconnectedError" || (err?.message || "").includes("DisconnectedError");
    if (isDisconnectedError && cable && isVisibleAndOnline()) {
      try {
        logger.debug("Cable kickCable retry after disconnect");
        Sentry.captureException("Cable kickCable retry after disconnect", {
          contexts: cableContexts(),
          extra: {
            disconnectError: error,
          },
        });
        cable.disconnect?.();
        await cable.connect?.();
      } catch (retryError) {
        logger.debug("Cable kickCable retry after disconnect failed", retryError);
        Sentry.captureException("Cable kickCable retry after disconnect failed", {
          contexts: cableContexts(),
          extra: {
            retryError,
            disconnectError: error,
          },
        });
      }
    }
  } finally {
    // Make sure we don't get stuck in "connecting" if connect throws before events fire.
    cableIsConnecting = false;
  }
}

export function getCable() {
  if (cableInstance) {
    return cableInstance;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  const url = process.env.NEXT_PUBLIC_CABLE_WEBSOCKET_URL;
  if (!url) {
    return undefined;
  }

  const cable = createCable({
    // logLevel: "debug",
    transportConfigurator: async (transport, { initial }) => {
      if (!initial) return;
      transport.setURL(getWebSocketUrl(url)!);
      await tokenRefresher(transport);
    },
    tokenRefresher,
  });

  cable.on("connect", (event: { reconnect?: boolean } = {}) => {
    logger.debug("Cable connected", {
      reconnect: event?.reconnect,
    });
    cableIsConnected = true;
    cableIsConnecting = false;
  });

  cable.on("disconnect", (...args: unknown[]) => {
    logger.debug("Cable disconnected", {
      args,
      visibilityState: getVisibilityState(),
      online: typeof navigator === "undefined" ? undefined : navigator.onLine,
    });

    cableIsConnected = false;
    cableIsConnecting = false;
  });

  cable.on("close", (...args: unknown[]) => {
    logger.debug("Cable closed", {
      args,
      visibilityState: getVisibilityState(),
      online: typeof navigator === "undefined" ? undefined : navigator.onLine,
    });

    cableIsConnected = false;
    cableIsConnecting = false;
  });

  cableInstance = cable;
  return cableInstance;
}

const listenCallbacks: Set<(payload: MessagePayload) => void> = new Set();

/**
 * Chat payloads the backend still broadcasts to the legacy document stream
 * alongside the chat stream. Chats subscribe to their own ChatChannel, so the
 * document channel copies are dropped to avoid double processing. The
 * document-channel `chat_updated` payload is NOT dropped — unlike its
 * chat-channel counterpart it carries the updated document.
 */
const CHAT_PAYLOAD_TYPES_ON_DOCUMENT_CHANNEL = new Set<MessagePayload["type"]>([
  "message_created",
  "message_updated",
  "message_deleted",
  "reaction_created",
  "reaction_updated",
  "reaction_deleted",
]);

const isMessagePayload = (msg: unknown): msg is MessagePayload =>
  typeof msg === "object" && msg !== null && typeof (msg as { type?: unknown }).type === "string";

/**
 * Subscribe to the cable channel.
 *
 * Incoming messages will be automatically normalized and added to `apiCache`,
 * so in many cases you don't need to update the data manually.
 *
 * Returns the subscription instance. You can use it to add custom callbacks if needed.
 * Be sure to call the `disconnect` function to unsubscribe from the channel
 * when it's no longer needed.
 *
 * Example:
 * ```
 * subscribe("DocumentChannel", { document_id: this.id })
 * ```
 */
export function subscribe<N extends keyof Channels>(channelName: N, channelParams?: Channels[N]) {
  if (!isAuthenticated()) return undefined;

  const cable = getCable();
  if (!cable) return undefined;

  const subscription = cable.subscribeTo(channelName, channelParams);

  subscription.on("message", (msg) => {
    if (
      channelName === "DocumentChannel" &&
      isMessagePayload(msg) &&
      CHAT_PAYLOAD_TYPES_ON_DOCUMENT_CHANNEL.has(msg.type)
    ) {
      return;
    }

    const payload = apiCache.set(msg);
    if (!isMessagePayload(payload)) return;

    for (const cb of listenCallbacks) {
      cb(payload);
    }
  });

  return subscription;
}

/**
 * Subscribe to all messages from any channel.
 * Returns an unsubscribe function — call it when the subscription is no longer needed
 * to avoid memory leaks.
 *
 * Example:
 * ```
 * subscribe() {
 *   this.cableUnsubscribe = onCableMessage((payload) => {
 *     if (payload.type === "message_created" && payload.chat_id === this.uid) {
 *       this.updateMessages([payload.message]);
 *     }
 *   });
 * }
 *
 * dispose() {
 *   this.cableUnsubscribe?.();
 * }
 * ```
 */
export function onCableMessage(callback: (payload: MessagePayload) => void): () => void {
  listenCallbacks.add(callback);
  return () => {
    listenCallbacks.delete(callback);
  };
}

export function onCableConnect(callback: (e: { reconnect?: boolean }) => void) {
  const cable = getCable();
  if (!cable) return undefined;
  const unsubscribe = cable.on("connect", (event) => {
    callback(event);
  });
  return unsubscribe;
}

export function onCableLost(callback: () => void) {
  const cable = getCable();
  if (!cable) return undefined;

  const unsubscribeDisconnect = cable.on("disconnect", () => {
    callback();
  });
  const unsubscribeClose = cable.on("close", () => {
    callback();
  });

  return () => {
    unsubscribeDisconnect();
    unsubscribeClose();
  };
}

export function getCableSubscriptionCount() {
  return {
    activeSubscriptions: cableInstance?.hub.size ?? 0,
    messageListeners: listenCallbacks.size,
  };
}

function cableContexts() {
  return { cable: getCableSubscriptionCount() };
}
